import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Users, Coins, TrendingUp, Globe, Hexagon, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

export default function DashboardPage() {
  const { user, token, API } = useAuth();
  const [stats, setStats] = useState(null);
  const [links, setLinks] = useState([]);
  const [showAllLinks, setShowAllLinks] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, linksRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/links/recent?limit=30`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(statsRes.data);
      setLinks(linksRes.data);
    } catch (e) { console.error(e); }
  }, [API, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statCards = [
    { icon: ShieldCheck, label: 'Links Protected', value: stats?.total_links_protected || 0, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    { icon: Users, label: 'Creators Helped', value: stats?.creators_supported || 0, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { icon: Coins, label: 'Total Points', value: stats?.total_points || 0, color: 'text-amber-400', bg: 'bg-amber-500/10' },
    { icon: TrendingUp, label: 'Current Level', value: stats?.current_level || 1, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  ];

  // Group links by domain
  const domainMap = {};
  links.forEach(l => {
    if (!domainMap[l.domain]) domainMap[l.domain] = { count: 0, creators: new Set() };
    domainMap[l.domain].count++;
    domainMap[l.domain].creators.add(l.creator_name);
  });
  const topDomains = Object.entries(domainMap)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 5);

  const visibleLinks = showAllLinks ? links : links.slice(0, 2);

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-base text-white">Your Stats</h2>
        <span className="text-[9px] text-zinc-500 font-mono">Updated just now</span>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-2">
        {statCards.map((card, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.08 }}
            className="glass-card rounded-xl p-3"
            data-testid={`dashboard-stat-${i}`}
          >
            <div className={`w-7 h-7 rounded-lg ${card.bg} flex items-center justify-center mb-1.5`}>
              <card.icon className={`w-3.5 h-3.5 ${card.color}`} />
            </div>
            <p className="font-heading font-black text-lg text-white">{card.value}</p>
            <p className="text-[9px] text-zinc-500">{card.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Top Sites */}
      {topDomains.length > 0 && (
        <div>
          <p className="text-xs font-bold text-zinc-400 mb-2">Top Protected Sites</p>
          <div className="space-y-1.5">
            {topDomains.slice(0, 3).map(([domain, data], i) => (
              <motion.div
                key={domain}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }}
                className="flex items-center gap-3 glass-card rounded-lg px-3 py-2.5"
                data-testid={`top-domain-${i}`}
              >
                <div className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-3.5 h-3.5 text-zinc-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white truncate">{domain}</p>
                  <p className="text-[9px] text-zinc-500">{data.creators.size} creator{data.creators.size > 1 ? 's' : ''}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-mono font-bold text-amber-500">{data.count}</p>
                  <p className="text-[9px] text-zinc-600">links</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Links - Show 2 by default */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-zinc-400">Recent Protected Links</p>
          {links.length > 0 && (
            <span className="text-[9px] text-zinc-600">{links.length} total</span>
          )}
        </div>
        
        {links.length === 0 ? (
          <div className="text-center py-6 glass-card rounded-xl">
            <ShieldCheck className="w-6 h-6 text-zinc-700 mx-auto mb-2" />
            <p className="text-xs text-zinc-600">No links protected yet</p>
            <p className="text-[10px] text-zinc-700">Start browsing to protect creator links!</p>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              {visibleLinks.map((link, i) => (
                <motion.div
                  key={link.id || i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                  data-testid={`recent-link-${i}`}
                >
                  <div className="w-6 h-6 rounded-md bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                    <Hexagon className="w-3 h-3 text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-[11px] font-medium text-white truncate">{link.creator_name}</p>
                      <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-500">{link.affiliate_network}</span>
                    </div>
                    <p className="text-[9px] text-zinc-600 truncate">{link.domain}</p>
                  </div>
                  <span className="text-[10px] font-mono text-amber-500/70">+{link.points_earned}</span>
                </motion.div>
              ))}
            </div>
            
            {links.length > 2 && (
              <button
                onClick={() => setShowAllLinks(!showAllLinks)}
                className="w-full mt-2 py-2 text-[10px] text-zinc-500 hover:text-zinc-400 flex items-center justify-center gap-1"
                data-testid="show-more-links"
              >
                {showAllLinks ? (
                  <>Show less <ChevronUp className="w-3 h-3" /></>
                ) : (
                  <>Show {links.length - 2} more <ChevronDown className="w-3 h-3" /></>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
